import React, { useState, useEffect, useRef } from "react";
import api from "../../Utils/api";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

const LEAVE_OPTIONS = [
  { id: "annual_leave", name: "Annual Leave" },
  { id: "bereavement_leave", name: "Bereavement (Compassionate) Leave" },
  { id: "community_service_leave", name: "Community Service Leave" },
  { id: "long_service_leave", name: "Long Service Leave" },
  { id: "other_paid_leave", name: "Other Paid Leave" },
  { id: "sick_leave", name: "Sick (Personal/Carer's) Leave" },
  { id: "time_off_in_lieu", name: "Time Off In Lieu" },
  { id: "unpaid_leave_leave", name: "Unpaid Leave - Leave" },
  { id: "unpaid_leave_no_show", name: "Unpaid Leave - No Show" },
  { id: "unpaid_leave_sick", name: "Unpaid Leave - Sick" },
];

function LeaveRequestDialog({ open, setOpen, getUserInfo, userID }) {
  const [leaveType, setLeaveType] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [localTime, setLocalTime] = useState("");
  const [reason, setReason] = useState("");

  const dialogRef = useRef(null);
  const firstFocusableRef = useRef(null);
  const lastFocusableRef = useRef(null);

  const user = useSelector((state) => state.user)?.user;

  // Trap focus inside the dialog when open
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "Escape") {
        setOpen(false);
      }
      if (e.key === "Tab" && open) {
        const focusableElements = dialogRef.current.querySelectorAll(
          "a[href], button:not([disabled]), textarea, input, select"
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    }

    if (open) {
      document.addEventListener("keydown", handleKeyDown);
      // Focus first input when dialog opens
      setTimeout(() => {
        firstFocusableRef.current?.focus();
      }, 0);
      // Prevent background scroll
      document.body.style.overflow = "hidden";
    } else {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Handle form submission logic here
    let sTime = new Date(startTime).getTime();
    let eTime = new Date(endTime).getTime();

    if (sTime > eTime) {
      toast.warn("Invalid Start/End time info!");
      return false;
    }

    const res = await api.post("/leave", {
      start: startTime + "Z",
      end: endTime + "Z",
      leaveType,
      leaveReason: reason,
      userID: userID,
      duration: 0,
    });

    if (res.data.success) {
      toast.success(res.data.message);
      getUserInfo(userID);
    } else {
      toast.error(res.data.message);
    }
    setOpen(false);
  };

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          aria-modal="true"
          role="dialog"
          aria-labelledby="dialog-title"
          aria-describedby="dialog-desc"
          onClick={() => setOpen(false)} // Close on overlay click
        >
          <div
            className="bg-white text-gray-600 rounded-lg max-w-md w-full p-6 relative"
            onClick={(e) => e.stopPropagation()} // Prevent close when clicking inside dialog
            ref={dialogRef}
          >
            <h2
              id="dialog-title"
              className="text-xl font-semibold mb-4 border-gray-700 pb-2"
            >
              Leave Request
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="leaveType" className="block mb-1 font-medium">
                  Leave Type
                </label>
                <select
                  id="leaveType"
                  ref={firstFocusableRef}
                  value={leaveType}
                  onChange={(e) => setLeaveType(e.target.value)}
                  required
                  className="w-full p-2 rounded bg-white text-gray-700 border border-gray-200  "
                >
                  <option value="" disabled>
                    Select leave type
                  </option>
                  {LEAVE_OPTIONS.map((item) => (
                    <option value={item.value}>{item.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="startTime" className="block mb-1 font-medium">
                  Start Time
                </label>
                <input
                  id="startTime"
                  type="datetime-local"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  required
                  className="w-full p-2 rounded bg-white text-gray-700 border border-gray-200  "
                />
              </div>

              <div>
                <label htmlFor="endTime" className="block mb-1 font-medium">
                  End Time
                </label>
                <input
                  id="endTime"
                  type="datetime-local"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  required
                  className="w-full p-2 rounded bg-white text-gray-700 border border-gray-200  "
                />
              </div>

              <div>
                <label htmlFor="reason" className="block mb-1 font-medium">
                  Reason
                </label>
                <textarea
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  placeholder="Enter reason for leave"
                  className="w-full p-2 rounded bg-white text-gray-700 border border-gray-200  "
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-gray-700">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="px-4 py-2 rounded bg-gray-600 text-white "
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  ref={lastFocusableRef}
                  className="px-4 py-2 rounded bg-green-600 text-white"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default LeaveRequestDialog;
